/**
 * Connects to the chat namespace as staff so we receive chat:unread_updated in real time.
 * When the backend emits unread_updated (after any message is sent), we dispatch chat:activity
 * so the Layout refetches group unread and ChatsPage can refetch customer list.
 * No polling delay – counts update immediately when a message is sent.
 */

import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_IO_URL } from '../lib/utils';

const CHAT_NAMESPACE = '/chat';

export default function useStaffUnreadSocket({ enabled = true }) {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      socketRef.current = null;
      return undefined;
    }

    const token = localStorage.getItem('company_admin_token');
    if (!token) {
      return undefined;
    }

    // Polling first then websocket avoids "closed before connection is established"
    // when React Strict Mode runs cleanup before the WS handshake completes.
    const socket = io(`${SOCKET_IO_URL}${CHAT_NAMESPACE}`, {
      auth: { token },
      query: { token },
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });

    socketRef.current = socket;

    socket.on('chat:unread_updated', () => {
      try {
        window.dispatchEvent(new Event('chat:activity'));
      } catch (e) {
        // ignore
      }
    });

    return () => {
      socket.removeAllListeners();
      // Avoid closing mid-handshake (causes "WebSocket is closed before the connection is established").
      // If already connected, disconnect now; otherwise disconnect once connected.
      if (socket.connected) {
        socket.disconnect();
      } else {
        socket.once('connect', () => socket.disconnect());
      }
      socketRef.current = null;
    };
  }, [enabled]);

  return {};
}
